/**
 * DashboardTabContent
 * Main dashboard view with Subject Mastery as hero
 */

import { useAuth } from '@/lib/auth';
import { useUserProfile } from '@/hooks/use-learning-profile';
import { SubjectMasteryCard } from '../SubjectMasteryCard';
import { FocusTodayCard } from '../FocusTodayCard';
import { cn } from '@/lib/utils';

// Helper to get greeting based on time of day
function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 17) return 'Good afternoon';
  return 'Good evening';
}

// Helper to get first name from profile, falling back to user metadata or email
function getFirstName(
  profileFirstName: string | null | undefined,
  email: string | undefined,
  fullName: string | undefined
): string {
  // Priority 1: Use first_name from profiles table (set during onboarding)
  if (profileFirstName) {
    return profileFirstName;
  }
  // Priority 2: Use full_name from user metadata (e.g., OAuth providers)
  if (fullName) {
    return fullName.split(' ')[0];
  }
  // Priority 3: Extract from email
  if (email) {
    const name = email.split('@')[0];
    // Capitalize first letter
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  return '';
}

interface DashboardTabContentProps {
  className?: string;
}

export function DashboardTabContent({ className }: DashboardTabContentProps) {
  const { user } = useAuth();
  const { data: userProfile } = useUserProfile();

  // Get user's first name for greeting (prioritize profiles.first_name from onboarding)
  const firstName = getFirstName(
    userProfile?.first_name,
    user?.email,
    user?.user_metadata?.full_name
  );
  const greeting = getGreeting();

  return (
    <div className={cn('space-y-6', className)}>
      {/* Page Title - Gold Standard Pattern */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {greeting}{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Here's what's on your plate today
        </p>
      </div>

      {/* Focus Today — highest-intent module, goes first */}
      <FocusTodayCard maxItems={5} />

      {/* Subject Mastery */}
      <SubjectMasteryCard />

      {/* TODO: Diagnostic + Momentum cards — restore when ready */}
    </div>
  );
}

export default DashboardTabContent;
